<script setup lang="ts">
import type { PropType } from 'vue'
import { computed, ref, onMounted, nextTick, watch } from 'vue'
import type { NavigationItem } from '../../models/navigation/NavigationItems.model'
import AppIcon from '~/components/elements/AppIcon.vue'
import { useLocalePath } from '#i18n'

const props = defineProps({
  item: {
    type: Object as PropType<NavigationItem>,
    required: true,
  },
  isCollapsed: {
    type: Boolean,
    default: false,
  },
  isItemActive: {
    type: Function,
    required: true,
  },
  tooltipPosition: {
    type: Object as PropType<{ top: number; left: number }>,
    default: () => ({ top: 0, left: 0 }),
  },
  pointerYOffset: {
    type: Number,
    default: 0,
  },
  expandedSubmenuContentTop: {
    type: Number,
    default: 0,
  },
  isKeyboardNavigation: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  closeSubmenu: []
  keyNav: [direction: 'parent' | 'escape']
  focusMainNavItem: [position: 'first' | 'last']
}>()

const localePath = useLocalePath()
const subMenuLinks = ref<HTMLElement[]>([])
const firstLinkRef = ref<HTMLElement | null>(null)
const isReady = ref(false)

const handleSubItemClick = () => {
  emit('closeSubmenu')
}

const submenuStyle = computed(() => {
  if (!props.item || !props.isCollapsed) return {}
  return {
    top: `${props.tooltipPosition?.top}px`,
    left: `${props.tooltipPosition?.left}px`,
  }
})

const expandedSubmenuStyle = computed(() => {
  if (!props.expandedSubmenuContentTop) return {}
  return {
    top: `${props.expandedSubmenuContentTop}px`,
  }
})

const handleAfterEnter = () => {
  if (props.isKeyboardNavigation) {
    focusFirstItem()
  }
}

const handleKeyDown = (event: KeyboardEvent, index: number) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (index < subMenuLinks.value.length - 1) {
        subMenuLinks.value[index + 1].focus()
      } else {
        emit('focusMainNavItem', 'first')
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      if (index > 0) {
        subMenuLinks.value[index - 1].focus()
      } else {
        emit('focusMainNavItem', 'last')
      }
      break
    case 'ArrowLeft':
      event.preventDefault()
      emit('keyNav', 'parent')
      break
    case 'Escape':
      event.preventDefault()
      emit('keyNav', 'escape')
      break
    case 'Tab':
      if (
        (event.shiftKey && index === 0) ||
        (!event.shiftKey && index === subMenuLinks.value.length - 1)
      ) {
        emit('closeSubmenu')
      }
      break
  }
}

const setSubmenuRef = (el: unknown, index: number) => {
  if (!el) return

  if (el instanceof HTMLElement) {
    if (index === 0) {
      firstLinkRef.value = el
    }
    subMenuLinks.value[index] = el
  }
}

const focusFirstItem = () => {
  if (!props.isKeyboardNavigation) return

  if (firstLinkRef.value) {
    setTimeout(() => {
      firstLinkRef.value?.focus()
    }, 50)
  } else {
    setTimeout(() => {
      updateSubMenuLinks()
      if (subMenuLinks.value.length > 0) {
        subMenuLinks.value[0].focus()
      }
    }, 100)
  }
}

const updateSubMenuLinks = () => {
  const selector = props.isCollapsed
    ? '[data-testid="desktop-collapsed-subitem-link"]'
    : '[data-testid="desktop-expanded-subitem-link"]'

  const containerSelector = props.isCollapsed
    ? '[data-testid="submenu-collapsed-flyout"]'
    : '[data-testid="submenu-expanded-slideover"]'

  const container = document.querySelector(containerSelector)
  if (container) {
    const links = container.querySelectorAll(selector)
    subMenuLinks.value = Array.from(links).filter(
      (el) => el instanceof HTMLElement
    ) as HTMLElement[]

    if (subMenuLinks.value.length > 0) {
      firstLinkRef.value = subMenuLinks.value[0]
    }

    isReady.value = true
  }
}

watch(
  () => props.item,
  () => {
    if (props.item) {
      isReady.value = false
      nextTick(() => {
        updateSubMenuLinks()
      })
    }
  },
  { immediate: true }
)

onMounted(async () => {
  await nextTick()
  updateSubMenuLinks()
})

defineExpose({
  focusFirstItem,
  handleAfterEnter,
})
</script>

<template>
  <Teleport to="body">
    <Transition
      v-if="item?.children"
      name="flyout-fade"
      enter-active-class="transition-opacity duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      @after-enter="handleAfterEnter"
    >
      <div
        v-if="isCollapsed"
        :style="submenuStyle"
        class="bg-white fixed z-50 w-52 rounded-xl p-2 shadow-lg ring-opacity-5"
        data-testid="submenu-collapsed-flyout"
        role="menu"
        aria-orientation="vertical"
        :aria-label="`${item?.label} submenu`"
      >
        <div
          class="absolute -left-1.5 -translate-y-1/2 transform"
          :style="{ top: pointerYOffset + 'px' }"
          aria-hidden="true"
          data-testid="submenu-pointer"
        >
          <div class="bg-white h-3 w-3 rotate-45 transform"></div>
        </div>
        <div
          class="px-3 py-2 text-base font-semibold text-black"
          data-testid="submenu-title"
          role="presentation"
        >
          {{ item?.label }}
        </div>
        <div class="space-y-1" data-testid="submenu-items">
          <NuxtLink
            v-for="(subItem, index) in item?.children"
            :key="subItem.label"
            :ref="(el) => setSubmenuRef(el, index)"
            :to="subItem.to ? localePath(subItem.to) : '#'"
            class="group flex items-center rounded-md px-3 py-2 text-base text-black hover:bg-primary-50"
            :class="{
              'font-bold': isItemActive(subItem),
              'focus:bg-primary-50 focus:outline-none focus:ring-[3px] focus:ring-black': true,
            }"
            :data-testid="`desktop-collapsed-subitem-link`"
            data-testid-index="`collapsed-submenu-item-${index}`"
            role="menuitem"
            tabindex="0"
            :aria-current="isItemActive(subItem) ? 'page' : undefined"
            @click="handleSubItemClick"
            @keydown="(e: KeyboardEvent) => handleKeyDown(e, index)"
          >
            <AppIcon
              :name="subItem.icon"
              size="sm"
              color="basics-icon"
              class="mr-3"
              aria-hidden="true"
            />
            <span>{{ subItem.label }}</span>
          </NuxtLink>
        </div>
      </div>

      <div
        v-else
        class="bg-white absolute left-[280px] top-[-4px] z-10 h-full w-64 border-l border-gray-50"
        data-testid="submenu-expanded-slideover"
        :aria-label="`${item?.label} submenu`"
      >
        <div class="absolute w-full" :style="expandedSubmenuStyle">
          <div class="p-4">
            <h3
              class="mb-3 px-3 text-base font-semibold text-black"
              data-testid="expanded-submenu-title"
            >
              {{ item?.label }}
            </h3>
            <div
              class="space-y-1"
              role="menu"
              aria-orientation="vertical"
              data-testid="expanded-submenu-items"
            >
              <NuxtLink
                v-for="(subItem, index) in item?.children"
                :key="subItem.label"
                :ref="(el) => setSubmenuRef(el, index)"
                :to="subItem.to ? localePath(subItem.to) : '#'"
                class="group flex items-center rounded-md px-3 py-2 text-base text-black hover:bg-primary-50 hover:font-bold"
                :class="{
                  'font-bold': isItemActive(subItem),
                  'focus:bg-primary-50 focus:outline-none focus:ring-[3px] focus:ring-black': true,
                }"
                :data-testid="`desktop-expanded-subitem-link`"
                data-testid-index="`expanded-submenu-item-${index}`"
                role="menuitem"
                tabindex="0"
                :aria-current="isItemActive(subItem) ? 'page' : undefined"
                @click="handleSubItemClick"
                @keydown="(e: KeyboardEvent) => handleKeyDown(e, index)"
              >
                <AppIcon
                  :name="subItem.icon"
                  size="sm"
                  color="basics-icon"
                  class="mr-3"
                  aria-hidden="true"
                />
                <span>{{ subItem.label }}</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
a[role='menuitem']:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.7);
}
</style>
